import {useRestAPI} from '@resurs-internal/rb-app-api';
import {useIsTouchDevice, useTranslate} from 'hooks';
import {captureException} from '@sentry/core';
import {useState} from 'react';
import {useAlertDialog} from 'components/AlertDialogProvider/AlertDialogProvider';

export const useOpenDocument = () => {
  const [isLoading, setIsLoading] = useState(false);
  const api = useRestAPI();
  const touch = useIsTouchDevice();
  const translate = useTranslate();
  const alerts = useAlertDialog();

  const url = async (
    url: string | undefined,
    fileName?: string,
    onError?: (e: unknown) => void,
  ) => {
    try {
      setIsLoading(true);
      // Links to documenthandler.resurs.com must be opened in a new tab
      // to avoid CORS issues
      if (url?.includes('documenthandler')) {
        window.open(url, '_blank');
        return;
      }
      const response = await api.client.fetchPDF(url);
      blob(response, fileName);
    } catch (e: unknown) {
      _error(e, onError);
    } finally {
      setIsLoading(false);
    }
  };

  const blob = (
    blob: Blob,
    fileName?: string,
    onError?: (e: unknown) => void,
  ) => {
    const url = URL.createObjectURL(blob);
    if (fileName) return download(url, fileName);
    if (url) return _open(url);
    _error(null, onError);
  };

  const download = (
    href: string,
    fileName = 'document.pdf',
    onError?: (e: unknown) => void,
  ) => {
    try {
      setIsLoading(true);
      const link = document.createElement('a');
      link.href = href;
      link.download = fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      if (link.parentNode === document.body) document.body.removeChild(link);
    } catch (error) {
      _error(error, onError);
    } finally {
      setIsLoading(false);
    }
  };

  const _open = (url: string) => {
    if (touch.isTouchDevice) window.location.href = url;
    else window.open(url, '_blank');
  };

  const _error = (e?: unknown, onError?: (e: unknown) => void) => {
    const error = e || new Error("Couldn't open blob");
    onError
      ? onError(error)
      : alerts.error({
          title: translate('common.documents.error.title'),
          subtitle: translate('common.documents.error.subtitle'),
          actions: [{title: translate('common.ok'), onClick: alerts.onClose}],
        });
    captureException(error);
  };

  return {url, blob, download, isLoading};
};
